import type { DatasetInstanceConfig } from './data-builder.store';

// Structural subset of the store state this helper reads. Compatible with
// `useDataBuilderStore.getState()`, which returns a superset.
type DataBuilderSubmitState = {
  datasetInstances: { instanceId: string; datasetKey: string }[];
  configByInstance: Record<string, DatasetInstanceConfig>;
  loadedSavedDatasetId: string | null;
  deliveryMethod: string;
  deliveryFormat: string;
};

const sum = (nums: number[]): number => nums.reduce((acc, n) => acc + n, 0);

/**
 * Derives the rich `data builder submitted` payload from the store state at
 * submit time. Per the tracking spec, ALL selection detail rides on this one
 * submission event (the funnel step events stay lightweight), so we can answer
 * "what actually gets built" — datasets, unit of analysis, how companies were
 * selected, single-vs-multi, and how much was customised — from one breakdown.
 */
export const buildDataBuilderSubmitProps = (
  state: DataBuilderSubmitState,
  extra: { isRevelioAdmin: boolean; submitVia: 'direct' | 'save' }
) => {
  const { datasetInstances, configByInstance } = state;
  const configs = datasetInstances
    .map((instance) => configByInstance[instance.instanceId])
    .filter((config): config is NonNullable<typeof config> => Boolean(config));

  const companySelectionMethods = [
    ...new Set(
      configs.map((config) => {
        if (config.companyMapper) return 'mapper';
        if (config.companySets && config.companySets.length > 0) {
          return 'predefined_group';
        }
        if (Object.keys(config.companySelections).length > 0) return 'manual';
        return 'none';
      })
    ),
  ];

  const totalCustomColumns = sum(
    configs.map((c) =>
      sum(Object.values(c.customColumnsByBreakdown).map((cols) => cols.length))
    )
  );
  const totalCustomFilters = sum(
    configs.map(
      (c) =>
        c.customFilters.length +
        sum(Object.values(c.customFiltersByBreakdown).map((f) => f.length))
    )
  );
  const totalColumnsSelected = sum(
    configs.map((c) =>
      sum(Object.values(c.columnKeysByBreakdown).map((keys) => keys.length))
    )
  );
  const totalCompanySelections = sum(
    configs.map((c) => Object.keys(c.companySelections).length)
  );
  const totalUoaSelections = sum(
    configs.map((c) => c.unitOfAnalysisSelections.length)
  );
  const unitsOfAnalysis = [
    ...new Set(
      configs
        .map((c) => c.unitOfAnalysis)
        .filter((uoa): uoa is string => uoa !== null)
    ),
  ];

  return {
    deliverable_count: datasetInstances.length,
    is_multi_dataset: datasetInstances.length > 1,
    dataset_keys: [...new Set(datasetInstances.map((i) => i.datasetKey))].join(
      ','
    ),
    units_of_analysis: unitsOfAnalysis.join(','),
    company_selection_methods: companySelectionMethods.join(','),
    company_selection_count: totalCompanySelections,
    uoa_selection_count: totalUoaSelections,
    is_multi_entity: totalCompanySelections + totalUoaSelections > 1,
    columns_selected_count: totalColumnsSelected,
    custom_column_count: totalCustomColumns,
    custom_filter_count: totalCustomFilters,
    has_custom_columns: totalCustomColumns > 0,
    has_custom_filters: totalCustomFilters > 0,
    is_from_saved_dataset: state.loadedSavedDatasetId !== null,
    delivery_method: state.deliveryMethod,
    delivery_format: state.deliveryFormat,
    is_revelio_admin: extra.isRevelioAdmin,
    submit_via: extra.submitVia,
    page: 'data builder',
    page_type: 'databuilder_flow',
  };
};
